import { useNode, useEditor, Element } from "@craftjs/core";
import React, { useRef } from "react";
import { Box, Typography } from "@mui/material";
import { ImageUpload } from "../ui/ImageUpload";
import { ImageCropPicker } from "../ui/ImageCropPicker";
import { useResponsive } from "../../hooks/useResponsive";
import { Text } from "./Text";
import { promotionBannerStyles } from "../../config/styleConfig";
import { selectedOutlineSx, labelStyle, itemCardStyle } from "../../config/settingsStyles";
import { TextField, SelectField, CheckboxField, ColorField, SettingsSection, SettingsRow } from "../ui/form/SettingsField";

export interface PromotionBannerProps {
  title: string;
  subtitle?: string;
  discount?: string;
  buttonText?: string;
  buttonLink?: string;
  backgroundImage?: string;
  backgroundPosition?: string;
  backgroundZoom?: number;
  backgroundColor?: string;
  accentColor?: string;
  textColor?: string;
  layout?: "left" | "center" | "right";
  showBadge?: boolean;
  badgeText?: string;
  expiryText?: string;
}

export const PromotionBanner = ({
  title = "Special Promotion!",
  subtitle = "Limited time offer for our valued customers",
  discount = "20% OFF",
  buttonText = "Book Now",
  buttonLink = "/contact",
  backgroundImage,
  backgroundPosition = "50% 50%",
  backgroundZoom = 1,
  backgroundColor = "#7c3aed",
  accentColor = "#fbbf24",
  textColor = "#ffffff",
  layout = "left",
  showBadge = true,
  badgeText = "Limited Time",
  expiryText = "Offer ends soon!",
}: PromotionBannerProps) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useResponsive(containerRef as React.RefObject<HTMLElement>);

  // Get responsive styles from config
  const padding = isMobile ? promotionBannerStyles.padding.mobile : promotionBannerStyles.padding.desktop;
  const discountFontSize = isMobile
    ? promotionBannerStyles.discount.mobile.fontSize
    : promotionBannerStyles.discount.desktop.fontSize;
  const titleFontSize = isMobile
    ? promotionBannerStyles.title.mobile.fontSize
    : promotionBannerStyles.title.desktop.fontSize;
  const subtitleFontSize = isMobile
    ? promotionBannerStyles.subtitle.mobile.fontSize
    : promotionBannerStyles.subtitle.desktop.fontSize;

  // Check if we're inside Craft.js editor context (CMS mode)
  const { inEditor } = useEditor((state) => ({
    inEditor: true,
  }));

  // Always prevent link navigation when in CMS editor
  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (inEditor) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const alignmentMap = {
    left: { textAlign: "left", alignItems: "flex-start" },
    center: { textAlign: "center", alignItems: "center" },
    right: { textAlign: "right", alignItems: "flex-end" },
  };

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) {
          connect(drag(ref));
          (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = ref;
        }
      }}
      sx={{
        position: "relative",
        overflow: "hidden",
        py: `${padding.py}px`,
        px: `${padding.px}px`,
        backgroundColor: backgroundImage ? undefined : backgroundColor,
        ...(selected && selectedOutlineSx),
      }}
    >
      {/* Background image with zoom/position */}
      {backgroundImage && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: backgroundPosition || "50% 50%",
            backgroundRepeat: "no-repeat",
            transform: `scale(${backgroundZoom || 1})`,
            transformOrigin: backgroundPosition || "50% 50%",
          }}
        />
      )}
      {/* Overlay for background image */}
      {backgroundImage && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: backgroundColor,
            opacity: 0.85,
          }}
        />
      )}

      {/* Decorative elements */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 256,
          height: 256,
          opacity: 0.1,
        }}
      >
        <svg viewBox="0 0 200 200" style={{ width: "100%", height: "100%" }}>
          <circle cx="100" cy="100" r="80" fill={accentColor} />
        </svg>
      </Box>
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: 192,
          height: 192,
          opacity: 0.1,
          transform: "translate(-25%, 25%)",
        }}
      >
        <svg viewBox="0 0 200 200" style={{ width: "100%", height: "100%" }}>
          <circle cx="100" cy="100" r="80" fill={accentColor} />
        </svg>
      </Box>

      {/* Content */}
      <Box
        sx={{
          position: "relative",
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          maxWidth: "896px",
          mx: "auto",
          ...alignmentMap[layout],
        }}
      >
        {/* Badge */}
        {showBadge && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 0.5,
              borderRadius: 12,
              fontSize: "0.875rem",
              fontWeight: 600,
              mb: 2,
              backgroundColor: accentColor,
              color: backgroundColor,
            }}
          >
            <Box component="svg" sx={{ width: 16, height: 16 }} fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </Box>
            {badgeText}
          </Box>
        )}

        {/* Discount Badge - using config */}
        {discount && (
          <Typography
            sx={{
              fontSize: discountFontSize,
              fontWeight: 900,
              mb: 1,
              color: accentColor,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {discount}
          </Typography>
        )}

        {/* Title - using linked node */}
        <Box sx={{ mb: 1 }}>
          <Element
            id="promo-title"
            is={Text}
            text={title}
            fontSize={24}
            fontWeight="bold"
            tagName="h3"
            color={textColor}
            textAlign={layout}
          />
        </Box>

        {/* Subtitle - using linked node */}
        <Box sx={{ mb: 2, opacity: 0.9 }}>
          <Element
            id="promo-subtitle"
            is={Text}
            text={subtitle || "Limited time offer for our valued customers"}
            fontSize={16}
            tagName="p"
            color={textColor}
            textAlign={layout}
          />
        </Box>

        {/* Expiry */}
        {expiryText && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: "0.875rem",
              mb: 3,
              color: accentColor,
            }}
          >
            <Box component="svg" sx={{ width: 16, height: 16 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </Box>
            {expiryText}
          </Box>
        )}

        {/* Button */}
        {buttonText && (
          <Box
            component="a"
            href={buttonLink || "#"}
            onClick={handleLinkClick}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 3,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              fontSize: "1.125rem",
              textDecoration: "none",
              transition: "all 0.2s",
              backgroundColor: accentColor,
              color: backgroundColor,
              "&:hover": {
                transform: "scale(1.05)",
                boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
              },
            }}
          >
            {buttonText}
            <Box component="svg" sx={{ width: 20, height: 20 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Settings Panel
export const PromotionBannerSettings = () => {
  const {
    actions: { setProp },
    discount,
    buttonText,
    buttonLink,
    backgroundImage,
    backgroundPosition,
    backgroundZoom,
    backgroundColor,
    accentColor,
    textColor,
    layout,
    showBadge,
    badgeText,
    expiryText,
  } = useNode((node) => ({
    discount: node.data.props.discount,
    buttonText: node.data.props.buttonText,
    buttonLink: node.data.props.buttonLink,
    backgroundImage: node.data.props.backgroundImage,
    backgroundPosition: node.data.props.backgroundPosition,
    backgroundZoom: node.data.props.backgroundZoom,
    backgroundColor: node.data.props.backgroundColor,
    accentColor: node.data.props.accentColor,
    textColor: node.data.props.textColor,
    layout: node.data.props.layout,
    showBadge: node.data.props.showBadge,
    badgeText: node.data.props.badgeText,
    expiryText: node.data.props.expiryText,
  }));

  return (
    <SettingsSection>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        💡 คลิกที่ตัวอักษรบน canvas เพื่อแก้ไข
      </Typography>

      <TextField
        label="Discount Text"
        value={discount}
        onChange={(v) => setProp((props: PromotionBannerProps) => (props.discount = v))}
        placeholder="20% OFF"
      />

      <div style={itemCardStyle}>
        <CheckboxField
          label="Show Badge"
          checked={showBadge}
          onChange={(v) => setProp((props: PromotionBannerProps) => (props.showBadge = v))}
        />
        {showBadge && (
          <TextField
            label="Badge Text"
            value={badgeText}
            onChange={(v) => setProp((props: PromotionBannerProps) => (props.badgeText = v))}
            placeholder="Limited Time"
          />
        )}
      </div>

      <TextField
        label="Expiry Text"
        value={expiryText}
        onChange={(v) => setProp((props: PromotionBannerProps) => (props.expiryText = v))}
        placeholder="Offer ends soon!"
      />

      <SettingsRow>
        <TextField
          label="Button Text"
          value={buttonText}
          onChange={(v) => setProp((props: PromotionBannerProps) => (props.buttonText = v))}
        />
        <TextField
          label="Button Link"
          value={buttonLink}
          onChange={(v) => setProp((props: PromotionBannerProps) => (props.buttonLink = v))}
        />
      </SettingsRow>

      <SelectField
        label="Layout"
        value={layout}
        onChange={(v) => setProp((props: PromotionBannerProps) => (props.layout = v as PromotionBannerProps["layout"]))}
        options={[
          { value: "left", label: "Left" },
          { value: "center", label: "Center" },
          { value: "right", label: "Right" },
        ]}
      />

      <div
        style={{
          borderTop: "1px solid #e5e7eb",
          paddingTop: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <label style={labelStyle}>Colors</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
          <ColorField
            label="Background"
            value={backgroundColor}
            onChange={(v) => setProp((props: PromotionBannerProps) => (props.backgroundColor = v))}
          />
          <ColorField
            label="Accent"
            value={accentColor}
            onChange={(v) => setProp((props: PromotionBannerProps) => (props.accentColor = v))}
          />
          <ColorField
            label="Text"
            value={textColor}
            onChange={(v) => setProp((props: PromotionBannerProps) => (props.textColor = v))}
          />
        </div>
      </div>

      <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "16px", paddingBottom: "20px", display: "flex", flexDirection: "column", gap: "12px" }}>
        <ImageUpload
          value={backgroundImage || ""}
          onChange={(url) => setProp((props: PromotionBannerProps) => (props.backgroundImage = url || undefined))}
          label="Background Image (optional)"
        />
        {backgroundImage && (
          <ImageCropPicker
            backgroundImage={backgroundImage}
            backgroundPosition={backgroundPosition}
            backgroundZoom={backgroundZoom}
            onPositionChange={(v) => setProp((props: PromotionBannerProps) => (props.backgroundPosition = v))}
            onZoomChange={(v) => setProp((props: PromotionBannerProps) => (props.backgroundZoom = v))}
          />
        )}
      </div>
    </SettingsSection>
  );
};

PromotionBanner.craft = {
  displayName: "Promotion",
  isCanvas: true,
  props: {
    title: "Special Promotion!",
    subtitle: "Limited time offer for our valued customers",
    discount: "20% OFF",
    buttonText: "Book Now",
    buttonLink: "/contact",
    backgroundColor: "#7c3aed",
    accentColor: "#fbbf24",
    textColor: "#ffffff",
    layout: "left",
    showBadge: true,
    badgeText: "Limited Time",
    expiryText: "Offer ends soon!",
  },
  rules: {
    canMoveIn: () => false,
    canMoveOut: () => false,
  },
  related: {
    settings: PromotionBannerSettings,
  },
};
